import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  clearAnalyticsStorage,
  CONSENT_EVENT,
  getConsentRecord,
  saveConsentChoice,
} from '../../lib/consent';

const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const CookieConsentBanner = () => {
  const [isOpen, setIsOpen] = useState(false);
  const bannerRef = useRef(null);
  const acceptRef = useRef(null);

  useEffect(() => {
    // Keep the banner out of prerendered HTML so it hydrates cleanly.
    setIsOpen(navigator.userAgent !== 'ReactSnap' && !getConsentRecord());

    const handlePreferences = (event) => {
      if (event.detail?.open) {
        setIsOpen(true);
      }
    };

    window.addEventListener(CONSENT_EVENT, handlePreferences);
    return () => window.removeEventListener(CONSENT_EVENT, handlePreferences);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const previousFocus = document.activeElement;
    window.setTimeout(() => acceptRef.current?.focus(), 0);

    const handleKeyDown = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(bannerRef.current?.querySelectorAll(focusableSelector) || []);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, [isOpen]);

  const choose = (choice) => {
    if (choice === 'rejected') clearAnalyticsStorage();
    saveConsentChoice(choice);
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-heading"
      aria-describedby="cookie-consent-description"
      className="fixed inset-x-0 bottom-0 z-[80] px-3 pb-3 text-slate-900 sm:px-5 sm:pb-5"
      data-testid="cookie-consent-banner"
    >
      <div className="mx-auto grid max-w-6xl gap-5 border border-[#0369A1] bg-white px-4 py-5 shadow-[0_-18px_70px_rgba(2,6,23,0.35)] sm:px-6 lg:grid-cols-[1fr_auto] lg:items-center">
        <div>
          <p id="cookie-consent-heading" className="font-['JetBrains_Mono'] text-xs font-bold uppercase tracking-[0.24em] text-[#0369A1]">
            Cookie preferences
          </p>
          <p id="cookie-consent-description" className="mt-2 max-w-3xl text-sm leading-7 text-slate-700">
            We use first-party analytics storage to understand page visits, quote form activity, and contact clicks. Analytics stays off unless you accept. You can change this later from Cookie preferences.
          </p>
          <Link to="/cookie-policy" className="mt-2 inline-flex min-h-11 items-center text-sm font-bold text-[#075985] hover:text-[#0f172a]">
            Read the cookie policy
          </Link>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:min-w-[23rem]">
          <Button
            ref={acceptRef}
            type="button"
            onClick={() => choose('accepted')}
            className="min-h-12 rounded-sm bg-[#0369A1] px-6 font-bold uppercase tracking-wider text-white hover:bg-[#075985]"
            data-testid="cookie-accept"
          >
            Accept all
          </Button>
          <Button
            type="button"
            onClick={() => choose('rejected')}
            className="min-h-12 rounded-sm border border-[#0B1F3B] bg-white px-6 font-bold uppercase tracking-wider text-[#0B1F3B] hover:bg-[#0B1F3B] hover:text-white"
            data-testid="cookie-reject"
          >
            Reject all
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
